import {APIClient} from '../utils/APIClient';
import {API_URL} from '../settings/local_settings';
import {uploadWithProgress} from '../utils/multipart';
import {notifyOnboardingProgress} from '../utils/onboardingProgress';

/**
 * El FormData de la subida de un archivo. Testeable aparte (patrón `buildFeedbackFormData`).
 *
 * Las claves opcionales viajan SÓLO si tienen valor: el backend rechaza un `document_kind` vacío,
 * y un `title: ''` pisaría el default que él deriva del nombre del archivo.
 */
export function buildUploadFormData({file, agencyClientId, title, documentKind, occurredOn,
    businessUnitId, durationSeconds}) {
    const formData = new FormData();
    formData.append('file', file, file && file.name);
    formData.append('agency_client_id', String(agencyClientId));
    // Trim: un título de puros espacios es "sin título", y mandarlo dejaría el documento rotulado
    // con un blanco en vez de derivarlo del nombre del archivo.
    if (title && title.trim()) formData.append('title', title.trim());
    if (documentKind) formData.append('document_kind', documentKind);
    if (occurredOn) formData.append('occurred_on', occurredOn);
    if (businessUnitId) formData.append('business_unit_id', String(businessUnitId));
    if (durationSeconds) formData.append('duration_seconds', String(Math.round(durationSeconds)));
    return formData;
}

// Capa API de la "Memoria del cliente" (tab de agencia). Espeja los endpoints del backend
// bajo `agent/` (ClientFact + ClientDocument). El curado de hechos es agency-only; el
// scope cliente↔agencia lo resuelve el backend (404 uniforme). `agency_client_id` va como
// query en GET/DELETE y en el body de POST/PATCH.
const BASE = API_URL + 'agent/';

export const ClientMemoryApi = {
    // --- Resumen (brief "qué sabe Dashbo de este cliente", ficha v2) ---
    // `options` pasa a `APIClient.call` (hoy sólo `{cache: true}`).
    // Desde la one-page del Recorrido TODOS sus consumidores lo piden con `{cache: true}`, y es a
    // propósito: el contenedor (que lo usa para el conteo del orbe y del umbral) y el Resumen
    // conviven ahora en el MISMO DOM, así que sin el flag serían dos veces el mismo request caro
    // —hasta 300 hechos, más notas, reuniones y conexiones—. Comparten el que está en vuelo.
    // Si alguna vista suma un "volver a cargar" explícito, ESE va sin cache: es el pedido que
    // necesita saltear los 30s justamente para mostrar lo recién curado.
    getMemoryBrief(agencyClientId, options = {}) {
        const url = `${BASE}client-memory-brief/?agency_client_id=${encodeURIComponent(agencyClientId)}`;
        return new APIClient(url).call('GET', {}, options);
    },

    // --- Paquete de Contexto (checklist "armá el contexto de este cliente") ---
    // GET agent/client-memory-context-pack/?agency_client_id= → {items:[{kind, priority,
    // destination, present, matched_documents}], core_present, core_total}. Listado: se detecta
    // por presencia de `items` (array), no por {status}. El backend resuelve el scope
    // cliente↔agencia + nivel (404 uniforme fuera de nivel/agencia).
    getContextPack(agencyClientId) {
        const url = `${BASE}client-memory-context-pack/?agency_client_id=${encodeURIComponent(agencyClientId)}`;
        return new APIClient(url).call('GET');
    },

    // --- Knob de autonomía: «¿Cuánto aprueba Dashbo solo?» (card 1EW5v3Ba) ---
    // Dos niveles: el default de la agencia (sólo admins lo editan) y el override por cliente
    // (lo edita quien cura la memoria de ese cliente). Los dos endpoints devuelven el envoltorio
    // {status, data} y sólo existen con la Memoria en nivel TOTAL — fuera de ahí, 404 uniforme.
    getAgencyMemoryAutonomy() {
        return new APIClient(`${BASE}memory-autonomy/`).call('GET');
    },

    // data: {memory_autonomy, can_edit}
    setAgencyMemoryAutonomy(memoryAutonomy) {
        return new APIClient(`${BASE}memory-autonomy/`).call('PUT', {memory_autonomy: memoryAutonomy});
    },

    // data: {agency, own, effective, inherited}
    getClientMemoryAutonomy(agencyClientId) {
        const url = `${BASE}client-memory-autonomy/?agency_client_id=${encodeURIComponent(agencyClientId)}`;
        return new APIClient(url).call('GET');
    },

    // `memoryAutonomy` null limpia el override y el cliente vuelve a seguir a la agencia. La clave
    // viaja SIEMPRE (aunque sea null): el backend distingue «heredá» de un request incompleto.
    setClientMemoryAutonomy(agencyClientId, memoryAutonomy) {
        return new APIClient(`${BASE}client-memory-autonomy/`).call('PUT', {
            agency_client_id: agencyClientId,
            memory_autonomy: memoryAutonomy === undefined ? null : memoryAutonomy,
        });
    },

    // --- Hechos (ClientFact) ---
    listFacts(agencyClientId, {reviewStatus, factType, autoArchived} = {}) {
        let url = `${BASE}client-fact/?agency_client_id=${encodeURIComponent(agencyClientId)}`;
        if (reviewStatus) url += `&review_status=${encodeURIComponent(reviewStatus)}`;
        if (factType) url += `&fact_type=${encodeURIComponent(factType)}`;
        // Sólo lo que archivó el triage («Archivado solo»): el server lo distingue de un rechazo humano.
        if (autoArchived) url += '&auto_archived=1';
        return new APIClient(url).call('GET');
    },

    createFact(payload) {
        return new APIClient(`${BASE}client-fact/`).call('POST', payload);
    },

    // Curado: `payload` lleva agency_client_id + (action 'approve'|'reject') y/o statement,
    // visibility, supersede_by_id.
    patchFact(factId, payload) {
        return new APIClient(`${BASE}client-fact/${factId}/`).call('PATCH', payload);
    },

    deleteFact(factId, agencyClientId) {
        const url = `${BASE}client-fact/${factId}/?agency_client_id=${encodeURIComponent(agencyClientId)}`;
        return new APIClient(url).call('DELETE');
    },

    // Curado en lote (mazo del Recorrido: "Aprobar todo", y el undo de esa misma tanda).
    // `action` ∈ 'approve' | 'reject' | 'reopen'. Es TODO-O-NADA en el backend: o entran los N o no
    // entra ninguno. Existe porque la alternativa —N PATCH en paralelo— son N requests y N
    // transacciones sueltas que pueden quedar a mitad de camino.
    //
    // ⚠️ Lo que NO hace, aunque una versión anterior de este comentario lo prometía: **no** deja una
    // sola fila en la Bitácora. Escribe UNA POR HECHO, deliberadamente, porque el embudo de adopción
    // del Command Center cuenta filas de log — con una fila por tanda, aprobar 20 de una y rechazar
    // 1 suelto sesgaba el `approval_rate` sin que nadie se enterara. La Bitácora las agrupa al
    // MOSTRAR (`memory_curation_burst`), que es donde corresponde resolverlo.
    // Sólo lectura: cuántos de los hechos que YA esperan habrían entrado solos con `memoryAutonomy`
    // (card 8uu3IdNd). Devuelve `{count, fact_ids, truncated}`; aprobar es otra llamada
    // (`bulkReviewFacts`), con el sí del usuario — el knob sigue sin ser retroactivo por su cuenta.
    previewAutonomyOnQueue(agencyClientId, memoryAutonomy) {
        const url = `${BASE}client-fact/autonomy-preview/`
            + `?agency_client_id=${encodeURIComponent(agencyClientId)}`
            + `&memory_autonomy=${encodeURIComponent(memoryAutonomy)}`;
        return new APIClient(url).call('GET');
    },

    /**
     * `skipMissing` afloja el todo-o-nada del backend SÓLO para quien lo pide: los ids que ya no
     * resuelven —o que dejaron de ser aprobables— se saltean y el resto entra, con `skipped` en la
     * respuesta. Lo usa la oferta del knob, cuyos ids salen de un GET anterior y por lo tanto pueden
     * envejecer entre las dos llamadas; «Aprobar todo» NO lo manda, porque sus ids salen del estado
     * vivo del mazo y ahí el todo-o-nada es la garantía correcta.
     */
    bulkReviewFacts(agencyClientId, action, factIds, {skipMissing = false} = {}) {
        const payload = {agency_client_id: agencyClientId, action, fact_ids: factIds};
        // Se manda la clave sólo cuando es `true`: un backend viejo la ignora igual, pero así el
        // request de los demás llamadores no cambia ni un byte.
        if (skipMissing) payload.skip_missing = true;
        return new APIClient(`${BASE}client-fact/bulk-review/`).call('POST', payload);
    },

    // Dedup asistido: grupos de posibles duplicados de la biblioteca (por similitud de embeddings).
    listMergeSuggestions(agencyClientId) {
        const url = `${BASE}client-fact/merge-suggestions/?agency_client_id=${encodeURIComponent(agencyClientId)}`;
        return new APIClient(url).call('GET');
    },

    // "No son duplicados": persiste el descarte de todos los pares del grupo.
    dismissMergeSuggestion(agencyClientId, factIds) {
        return new APIClient(`${BASE}client-fact/merge-suggestions/dismiss/`)
            .call('POST', {agency_client_id: agencyClientId, fact_ids: factIds});
    },

    // Motor de duplicados de la biblioteca (2026-09-12): lo que el backend ya decidió por PAR —las
    // propuestas abiertas («Dicen lo mismo» / «Cambió» / «Se contradicen»), las fusiones automáticas
    // que todavía se pueden deshacer y tres números—. Sólo lectura: acá no se calcula nada. En el
    // Booster reemplaza a `listMergeSuggestions`, que queda para la Memoria clásica.
    listDuplicates(agencyClientId) {
        const url = `${BASE}client-fact/duplicates/?agency_client_id=${encodeURIComponent(agencyClientId)}`;
        return new APIClient(url).call('GET');
    },

    // Una persona cierra una propuesta. `action` ∈ 'merge' | 'keep_newer' | 'keep_older' |
    // 'keep_both' | 'undo'; `extra` lleva `survivor_id` (y `statement` opcional) sólo con `merge`.
    // La fusión es UNA transacción del backend con su propia fila de Bitácora — no los N PATCH
    // paralelos sin rollback que hacía la sección vieja.
    resolveDuplicate(verdictId, agencyClientId, action, extra = {}) {
        return new APIClient(`${BASE}client-fact/duplicates/${verdictId}/resolve/`)
            .call('POST', {agency_client_id: agencyClientId, action, ...extra});
    },

    // --- Documentos (ClientDocument) — crudo de la memoria. Agency-only.
    // El listado es liviano (sin el crudo); el detalle trae raw_text.
    listDocuments(agencyClientId) {
        const url = `${BASE}client-document/?agency_client_id=${encodeURIComponent(agencyClientId)}`;
        return new APIClient(url).call('GET');
    },

    createDocument(payload, {requestKey} = {}) {
        // Cargar memoria completa un paso de «Tus primeros pasos»: el drawer se tilda solo.
        return new APIClient(`${BASE}client-document/`).call('POST', payload, {omitBodyFromReports: true,
            extraHeaders: requestKey ? {'Idempotency-Key': requestKey} : {}}).then(notifyOnboardingProgress);
    },

    // Subida de un archivo BINARIO (PDF / Word / imagen / audio). Un archivo por request; el
    // `source` (UPLOAD) lo estampa el backend. Va por multipart con XHR para tener progreso —
    // `APIClient` no puede. `onProgress(fraction)` y `signal` (cancelación) son opcionales.
    // Nunca rechaza: resuelve el mismo shape que `APIClient` (`status`, `reason_code`, `raw_body`).
    uploadDocumentFile({file, agencyClientId, title, documentKind, occurredOn, businessUnitId,
        durationSeconds, onProgress, signal, requestKey}) {
        const formData = buildUploadFormData({
            file, agencyClientId, title, documentKind, occurredOn, businessUnitId, durationSeconds,
        });
        return uploadWithProgress(`${BASE}client-document/upload/`, formData, {onProgress, signal, extraHeaders: requestKey ? {'Idempotency-Key': requestKey} : {}})
            .then(notifyOnboardingProgress);
    },

    getDocument(documentId, agencyClientId) {
        const url = `${BASE}client-document/${documentId}/?agency_client_id=${encodeURIComponent(agencyClientId)}`;
        return new APIClient(url).call('GET');
    },

    // Qué se llevaría puesto el borrado de este documento: `{facts, facts_kept, notes, events,
    // facts_available}`. Se pide al abrir la confirmación para que la pregunta diga números en vez
    // de una advertencia genérica. Si falla, la pantalla ofrece igual las dos opciones sin cifras.
    getDeletionImpact(documentId, agencyClientId) {
        const url = `${BASE}client-document/${documentId}/deletion-impact/?agency_client_id=${encodeURIComponent(agencyClientId)}`;
        return new APIClient(url).call('GET');
    },

    // `withExtracted` borra también lo que se extrajo del documento (hechos, resumen y eventos).
    // Viaja por QUERY y no por body: un DELETE con cuerpo lo permite el estándar, pero la opción
    // no puede depender de eso — si se perdiera en el camino borraría de menos EN SILENCIO.
    deleteDocument(documentId, agencyClientId, withExtracted = false) {
        const extra = withExtracted ? '&with_extracted=true' : '';
        const url = `${BASE}client-document/${documentId}/?agency_client_id=${encodeURIComponent(agencyClientId)}${extra}`;
        return new APIClient(url).call('DELETE');
    },

    // URL firmada, de vida corta, para escuchar/ver el archivo ORIGINAL de un documento subido.
    // Se pide al momento de reproducir y NO se cachea: vence a los minutos, así que una URL
    // guardada es una URL vencida. Devuelve `null` si no se pudo preparar — la pantalla ofrece el
    // resto de la ficha sin el reproductor.
    getDocumentMediaUrl(documentId, agencyClientId) {
        const url = `${BASE}client-document/${documentId}/media-url/?agency_client_id=${encodeURIComponent(agencyClientId)}`;
        return new APIClient(url).call('GET').then((response) => {
            // La rama de error se escribe `!== 'OK'`: el backend manda su código tipado en
            // `status`, así que un `=== 'ERROR'` se come en silencio todo lo demás.
            if (!response || response.status !== 'OK' || !response.data) return null;
            return response.data;
        });
    },

    retryDocument(documentId, agencyClientId) {
        return new APIClient(`${BASE}client-document/${documentId}/retry/`)
            .call('POST', {agency_client_id: agencyClientId});
    },

    // Vuelve a leer con el extractor de HOY un documento que ya terminó bien. `confirm` es
    // obligatorio del lado del backend: re-procesar gasta crédito de IA otra vez (en audio, dos
    // veces: transcribir y extraer) y puede duplicar hechos, así que no puede salir de un doble
    // click. La pantalla muestra las dos consecuencias antes de llamar acá.
    reprocessDocument(documentId, agencyClientId, {documentKind, expectedRunId} = {}) {
        return new APIClient(`${BASE}client-document/${documentId}/reprocess/`)
            .call('POST', {agency_client_id: agencyClientId, confirm: true,
                ...(documentKind ? {document_kind: documentKind} : {}),
                ...(expectedRunId !== undefined ? {expected_run_id: expectedRunId} : {})});
    },

    // Vuelve a leer la página de un documento cargado desde un enlace. Devuelve
    // {status:'OK', data} si cambió (re-extrae) o {status:'UNCHANGED', data} si no. Si la página
    // trajo mucho menos texto, un 400 `CONTENT_SHRUNK` (con chars_before/chars_after) frena el
    // pisado del crudo; `force` reintenta salteando ESE guard, no los demás.
    refreshUrlDocument(documentId, agencyClientId, force = false) {
        const payload = {agency_client_id: agencyClientId};
        if (force) payload.force = true;
        return new APIClient(`${BASE}client-document/${documentId}/refresh-url/`)
            .call('POST', payload);
    },

    // Corrige el tipo de material de un documento ya cargado (Paquete de Contexto). Queda declarado
    // por la agencia, así que ninguna re-extracción posterior lo pisa.
    patchDocumentKind(documentId, agencyClientId, documentKind) {
        return new APIClient(`${BASE}client-document/${documentId}/`)
            .call('PATCH', {agency_client_id: agencyClientId, document_kind: documentKind});
    },

    // --- Notas (ClientMemory) — captura manual humano<->IA. Agency ve todo; el listado
    // devuelve el array directo ({memories}), las mutaciones {status:OK, data}.
    listMemories(agencyClientId) {
        const url = `${BASE}client-memory/?agency_client_id=${encodeURIComponent(agencyClientId)}`;
        return new APIClient(url).call('GET');
    },

    createMemory(payload) {
        return new APIClient(`${BASE}client-memory/`).call('POST', payload).then(notifyOnboardingProgress);
    },

    patchMemory(memoryId, payload) {
        return new APIClient(`${BASE}client-memory/${memoryId}/`).call('PATCH', payload);
    },

    deleteMemory(memoryId, agencyClientId) {
        const url = `${BASE}client-memory/${memoryId}/?agency_client_id=${encodeURIComponent(agencyClientId)}`;
        return new APIClient(url).call('DELETE');
    },

    // --- El pulso de los últimos N días (el sismógrafo del acto Memoria).
    // Ojo: NO cuelga de `agent/` sino de la familia de la Bitácora (`client/<id>/activity-log/`),
    // que es de donde salen los datos y de donde hereda la resolución de acceso.
    // Devuelve SIEMPRE N posiciones, con los días sin actividad en 0: un array corto obligaría a
    // cada consumidor a reconstruir los huecos, y ahí es donde los ejes empiezan a mentir.
    // `categories` (CSV) sigue el filtro de capas activo de la bandeja.
    // `serverFilters` (query string ya armada por la Bitácora: quién / cuenta / tipo de evento)
    // va tal cual, sin re-armarla: el pulso tiene que contar EXACTAMENTE lo que la lista muestra,
    // y dos armados distintos del mismo filtro es como se rompió esa igualdad las veces anteriores.
    getActivityDailyCounts(clientId, {days = 14, tzOffset, categories, serverFilters} = {}) {
        let url = `${API_URL}client/${clientId}/activity-log/daily-counts/?days=${encodeURIComponent(days)}`;
        if (tzOffset !== undefined && tzOffset !== null) url += `&tz_offset=${encodeURIComponent(tzOffset)}`;
        if (categories) url += `&categories=${encodeURIComponent(categories)}`;
        if (serverFilters) url += `&${serverFilters}`;
        return new APIClient(url).call('GET');
    },

    // --- El contexto literal que recibe el asistente ("Como lo ve tu IA").
    // Es el MISMO endpoint que consume el MCP público, no una reconstrucción: si esta hoja
    // mostrara algo armado en el frontend, diría lo que creemos que ve la IA en vez de lo que ve.
    // Devuelve secciones (profile, goals, facts, business_units, memories, recent_events,
    // documents) con sus caps y sus flags `*_truncated`.
    // SIN cache a propósito: se abre justo después de curar, y 30s de cache esconderían el hecho
    // que el usuario acaba de aprobar — que es exactamente lo que vino a comprobar.
    getClientContext(agencyClientId) {
        const url = `${BASE}get_client_context/?agency_client_id=${encodeURIComponent(agencyClientId)}`;
        return new APIClient(url).call('GET');
    },

    // --- Portal del cliente (read-only): SOLO lo compartido (hechos+notas), serializado
    // client-safe por el backend. Sirve tanto al cliente real como al preview de agencia.
    portalMemory(agencyClientId) {
        const url = `${BASE}portal-memory/?agency_client_id=${encodeURIComponent(agencyClientId)}`;
        return new APIClient(url).call('GET');
    },

    // --- Conexiones de fuentes (Drive) — vincular una carpeta de Drive al cliente. Agency-only.
    // `getDriveConfig`/`exchangeDriveCode` devuelven el objeto directo (sin envoltorio {status});
    // `listConnections` devuelve {connections}; las mutaciones {status:OK, data}.
    getDriveConfig() {
        return new APIClient(`${BASE}source-connection/drive/config/`).call('GET');
    },

    exchangeDriveCode(code, redirectUri) {
        const payload = {code};
        if (redirectUri) payload.redirect_uri = redirectUri;
        return new APIClient(`${BASE}source-connection/drive/exchange/`).call('POST', payload);
    },

    // `purpose` (opcional): MEMORY (default del backend) o MEDIA_PLAN — el listado siempre viene
    // scopeado por propósito, así el tab Memoria y la pantalla de media plan no se mezclan.
    listConnections(agencyClientId, purpose) {
        let url = `${BASE}source-connection/?agency_client_id=${encodeURIComponent(agencyClientId)}`;
        if (purpose) url += `&purpose=${encodeURIComponent(purpose)}`;
        return new APIClient(url).call('GET');
    },

    // Valida los archivos elegidos en el Picker SIN crear nada: por cada archivo devuelve
    // {id, name, accepted, reason_code} (files().get server-side + admisión). Para el preview de
    // "qué entra" antes de confirmar.
    validateFiles(payload) {
        return new APIClient(`${BASE}source-connection/validate/`).call('POST', payload);
    },

    createConnection(payload) {
        // Conectar un media plan enciende un power up del drawer.
        return new APIClient(`${BASE}source-connection/`).call('POST', payload).then(notifyOnboardingProgress);
    },

    syncConnection(connectionId, agencyClientId) {
        return new APIClient(`${BASE}source-connection/${connectionId}/sync/`)
            .call('POST', {agency_client_id: agencyClientId});
    },

    deleteConnection(connectionId, agencyClientId) {
        const url = `${BASE}source-connection/${connectionId}/?agency_client_id=${encodeURIComponent(agencyClientId)}`;
        return new APIClient(url).call('DELETE');
    },
};
